import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Manage a draggable size for a pane in an outlook-style split view. The size
 * is persisted per `storageKey` in localStorage so each screen — and each
 * orientation — remembers its own preference across reloads.
 *
 * `axis: 'x'` (default) redimensiona el ANCHO del panel de la izquierda;
 * `axis: 'y'` redimensiona el ALTO del panel de arriba. Es un solo hook y no
 * dos porque lo único que cambia es qué coordenada del ratón se mira y qué
 * cursor se pinta: duplicarlo era garantizar que un arreglo entrara en una de
 * las dos copias, igual que pasó con las etiquetas de rol.
 *
 * ⚠️ La `storageKey` tiene que ser DISTINTA por eje. Los rangos no se parecen
 * (240–600px de ancho, 160–700px de alto) y compartir clave significa que
 * cambiar de vista te devuelve un tamaño que el otro eje descarta por fuera de
 * rango — o peor, que acepta y deja el panel absurdo.
 *
 * Usage:
 *   const { size, resizerProps } = useResizablePane('incidencias:listPane');
 *   <div className="listPane" style={{ width: size }}>...</div>
 *   <div className="resizer" {...resizerProps} />
 */
export function useResizablePane(
  storageKey: string,
  opts: { defaultSize?: number; min?: number; max?: number; axis?: 'x' | 'y' } = {},
) {
  const axis = opts.axis ?? 'x';
  const defaultSize = opts.defaultSize ?? 320;
  const min = opts.min ?? 240;
  const max = opts.max ?? 600;

  const [size, setSize] = useState<number>(() => {
    if (typeof window === 'undefined') return defaultSize;
    const stored = window.localStorage.getItem(storageKey);
    const n = stored ? Number.parseInt(stored, 10) : NaN;
    return Number.isFinite(n) && n >= min && n <= max ? n : defaultSize;
  });

  // Persist whenever size changes (skip mount — only real user edits).
  const isFirst = useRef(true);
  useEffect(() => {
    if (isFirst.current) { isFirst.current = false; return; }
    window.localStorage.setItem(storageKey, String(Math.round(size)));
  }, [storageKey, size]);

  // --- Drag logic ----------------------------------------------------
  const dragState = useRef<{ start: number; startSize: number } | null>(null);

  const onMouseMove = useCallback((e: MouseEvent) => {
    if (!dragState.current) return;
    const { start, startSize } = dragState.current;
    const pointer = axis === 'x' ? e.clientX : e.clientY;
    const next = Math.min(max, Math.max(min, startSize + (pointer - start)));
    setSize(next);
  }, [min, max, axis]);

  const onMouseUp = useCallback(() => {
    dragState.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
  }, [onMouseMove]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragState.current = {
      start: axis === 'x' ? e.clientX : e.clientY,
      startSize: size,
    };
    document.body.style.cursor = axis === 'x' ? 'col-resize' : 'row-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [size, axis, onMouseMove, onMouseUp]);

  const reset = useCallback(() => setSize(defaultSize), [defaultSize]);

  // Cleanup if component unmounts mid-drag
  useEffect(() => () => {
    if (dragState.current) {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    }
  }, [onMouseMove, onMouseUp]);

  return {
    size,
    reset,
    resizerProps: {
      onMouseDown,
      onDoubleClick: reset,
      role: 'separator' as const,
      /* ⚠️ `aria-orientation` es la del SEPARADOR, no la del eje que mueve, y
         son perpendiculares: un separador que ajusta anchos es una línea
         VERTICAL. Ponerlo al revés es el error fácil aquí. */
      'aria-orientation': (axis === 'x' ? 'vertical' : 'horizontal') as 'vertical' | 'horizontal',
      'data-axis': axis,
      title: 'Arrastrar para redimensionar (doble-click para reset)',
    },
  };
}
